import { useState, useCallback, useMemo, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MessageCircle,
  AlertCircle,
  User,
  Bot,
  MessageSquare,
  Search,
  ArrowUp,
  ArrowDown,
  X,
} from 'lucide-react'
import { useRawRecords, useAttendantsList } from '@/hooks/useAttendanceMetrics'
import type { ProtocolMessage, RawRecordsFilters } from '@/types/attendance-metrics'

function formatDateTime(isoDate: string): string {
  const date = new Date(isoDate)
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function truncateMessage(text: string | null, maxLength: number = 50): string {
  if (!text) return '-'

  // Remove attendant name prefix pattern: "*Name* " at the start
  const cleanedText = text.replace(/^\*[^*]+\*\s*/, '')

  if (cleanedText.length <= maxLength) return cleanedText
  return cleanedText.slice(0, maxLength) + '...'
}

// Helper to check if userId is empty (null, undefined, or empty string)
function isUserIdEmpty(userId: string | null | undefined): boolean {
  return userId === null || userId === undefined || userId === ''
}

type SenderType = 'attendant' | 'bot' | 'client'

type SenderInfo = {
  type: SenderType
  label: string
  badgeClass: string
  icon: typeof User | typeof Bot | typeof MessageSquare
}

function getSenderInfo(record: ProtocolMessage): SenderInfo {
  if (record.fromMe && !isUserIdEmpty(record.userId)) {
    // Atendente: name will be in separate column
    return {
      type: 'attendant',
      label: 'Atendente',
      badgeClass: 'bg-primary text-primary-foreground',
      icon: User,
    }
  } else if (record.fromMe && isUserIdEmpty(record.userId)) {
    // Bot/Sistema: purple color for visibility
    return {
      type: 'bot',
      label: 'Bot/Sistema',
      badgeClass: 'bg-purple-600 text-white dark:bg-purple-500',
      icon: Bot,
    }
  } else {
    // Cliente
    return {
      type: 'client',
      label: 'Cliente',
      badgeClass: 'bg-transparent border border-border text-foreground',
      icon: MessageSquare,
    }
  }
}

interface RawRecordsTableProps {
  className?: string
  refreshKey?: number
}

export function RawRecordsTable({ className, refreshKey }: RawRecordsTableProps) {
  // Filter state
  const [page, setPage] = useState(1)
  const [ticketId, setTicketId] = useState('')
  const [protocolId, setProtocolId] = useState('')
  const [userId, setUserId] = useState('')
  const [senderType, setSenderType] = useState<string>('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const limit = 20

  // Build filters object
  const filters: RawRecordsFilters = useMemo(() => {
    const f: RawRecordsFilters = {
      page,
      limit,
      sortOrder,
    }

    if (ticketId.trim()) f.ticketId = ticketId.trim()
    if (protocolId.trim()) f.protocolId = protocolId.trim()
    if (userId.trim()) f.userId = userId.trim()
    if (startDate) f.startDate = startDate
    if (endDate) f.endDate = endDate
    if (senderType !== 'all') f.senderType = senderType as 'attendant' | 'bot' | 'client'

    return f
  }, [page, limit, sortOrder, ticketId, protocolId, userId, startDate, endDate, senderType])

  const { data, isLoading, error, refetch } = useRawRecords(filters)
  const { data: attendants, isLoading: isLoadingAttendants } = useAttendantsList()

  // Trigger refetch when refreshKey changes
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      refetch()
    }
  }, [refreshKey, refetch])

  const totalPages = data?.pagination.totalPages || 0
  const hasNextPage = page < totalPages
  const hasPrevPage = page > 1

  const handleClearFilters = useCallback(() => {
    setTicketId('')
    setProtocolId('')
    setUserId('')
    setSenderType('all')
    setStartDate('')
    setEndDate('')
    setPage(1)
  }, [])

  const hasActiveFilters = ticketId || protocolId || userId || senderType !== 'all' || startDate || endDate

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))
    setPage(1)
  }

  // Reset page when filters change
  const handleFilterChange = (setter: (value: string) => void) => (value: string) => {
    setter(value)
    setPage(1)
  }

  // Loading state (skeleton)
  if (isLoading) {
    return (
      <div className={className}>
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={className}>
        <div className="flex flex-col items-center justify-center py-12 gap-4 border rounded-lg bg-muted/30">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <div className="text-center">
            <p className="font-medium">Erro ao Carregar Mensagens</p>
            <p className="text-sm text-muted-foreground">
              Não foi possível carregar as mensagens. Tente novamente.
            </p>
          </div>
          <Button onClick={() => refetch()} variant="outline" size="sm">
            Tentar Novamente
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="space-y-4">
        {/* Filters - always visible */}
        <div className="space-y-3">
          {/* Filter row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 p-4 rounded-lg border bg-muted/30">
            {/* Ticket ID */}
            <div className="space-y-1.5">
              <Label htmlFor="ticketId" className="text-xs">
                Ticket
              </Label>
              <Input
                id="ticketId"
                placeholder="Ex: 22942"
                value={ticketId}
                onChange={(e) => handleFilterChange(setTicketId)(e.target.value)}
                className="h-9"
              />
            </div>

            {/* Protocol ID */}
            <div className="space-y-1.5">
              <Label htmlFor="protocolId" className="text-xs">
                Protocolo
              </Label>
              <Input
                id="protocolId"
                placeholder="Ex: 77622"
                value={protocolId}
                onChange={(e) => handleFilterChange(setProtocolId)(e.target.value)}
                className="h-9"
              />
            </div>

            {/* Attendant */}
            <div className="space-y-1.5">
              <Label htmlFor="userId" className="text-xs">
                Atendente
              </Label>
              <Select value={userId || 'all'} onValueChange={(value) => handleFilterChange(setUserId)(value === 'all' ? '' : value)}>
                <SelectTrigger id="userId" className="h-9" disabled={isLoadingAttendants}>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {attendants?.map((attendant) => (
                    <SelectItem key={attendant.userId} value={attendant.userId}>
                      {attendant.userName} #{attendant.userId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sender Type */}
            <div className="space-y-1.5">
              <Label htmlFor="senderType" className="text-xs">
                Remetente
              </Label>
              <Select value={senderType} onValueChange={handleFilterChange(setSenderType)}>
                <SelectTrigger id="senderType" className="h-9">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="attendant">Atendentes</SelectItem>
                  <SelectItem value="client">Clientes</SelectItem>
                  <SelectItem value="bot">Bot/Sistema</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Start Date */}
            <div className="space-y-1.5">
              <Label htmlFor="startDate" className="text-xs">
                Data Início
              </Label>
              <Input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => handleFilterChange(setStartDate)(e.target.value)}
                className="h-9"
              />
            </div>

            {/* End Date */}
            <div className="space-y-1.5">
              <Label htmlFor="endDate" className="text-xs">
                Data Fim
              </Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => handleFilterChange(setEndDate)(e.target.value)}
                className="h-9"
              />
            </div>
          </div>

          {/* Summary row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MessageCircle className="h-4 w-4" />
              <span>
                {data?.pagination.total.toLocaleString() || 0} mensagen
                {data?.pagination.total !== 1 ? 's' : ''}
              </span>
              <span className="text-muted-foreground/50">·</span>
              <span>
                Página {page} de {totalPages || 1}
              </span>
            </div>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                className="gap-1 text-muted-foreground h-8"
              >
                <X className="h-4 w-4" />
                Limpar filtros
              </Button>
            )}
          </div>
        </div>

        {/* Empty state */}
        {!data || data.records.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3 border rounded-lg bg-muted/30">
            <Search className="h-12 w-12 text-muted-foreground" />
            <p className="text-sm font-medium">
              {hasActiveFilters
                ? 'Nenhuma mensagem encontrada com esses filtros'
                : 'Nenhuma mensagem encontrada'}
            </p>
            <p className="text-xs text-muted-foreground text-center max-w-sm">
              {hasActiveFilters
                ? 'Tente ajustar os filtros ou limpar a busca'
                : 'As mensagens aparecem aqui após uma importação de dados do Whaticket'}
            </p>
            {hasActiveFilters && (
              <Button variant="outline" size="sm" onClick={handleClearFilters}>
                Limpar filtros
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-[100px]">Protocolo</TableHead>
                    <TableHead className="w-[100px]">Ticket</TableHead>
                    <TableHead className="w-[140px]">Atendente</TableHead>
                    <TableHead className="w-[120px]">Remetente</TableHead>
                    <TableHead>Mensagem</TableHead>
                    <TableHead className="w-[150px]">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="-ml-3 h-8 gap-1"
                        onClick={toggleSortOrder}
                      >
                        Data/Hora
                        {sortOrder === 'desc' ? (
                          <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUp className="h-3 w-3" />
                        )}
                      </Button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.records.map((record) => {
                    const sender = getSenderInfo(record)
                    return (
                      <TableRow key={record.id} className="hover:bg-muted/30">
                        <TableCell className="font-mono text-xs">
                          {record.whaticketProtocolId}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {record.whaticketTicketId}
                        </TableCell>
                        <TableCell className="text-sm">
                          {record.userName && record.userId
                            ? `${record.userName} #${record.userId}`
                            : record.userName || '-'}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-normal ${sender.badgeClass}`}
                          >
                            {sender.label}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-[400px]">
                          <p className="text-sm truncate" title={record.messageBody || ''}>
                            {truncateMessage(record.messageBody, 80)}
                          </p>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {formatDateTime(record.messageTimestamp)}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {data.records.map((record) => {
                const sender = getSenderInfo(record)
                const SenderIcon = sender.icon
                return (
                  <Card key={record.id} className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <SenderIcon className="h-4 w-4 text-muted-foreground" />
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-normal ${sender.badgeClass}`}
                          >
                            {sender.label}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(record.messageTimestamp)}
                        </span>
                      </div>

                      <p className="text-sm">{truncateMessage(record.messageBody, 100)}</p>

                      <div className="flex flex-col gap-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-3">
                          <span>
                            Prot: <span className="font-mono">{record.whaticketProtocolId}</span>
                          </span>
                          <span>
                            Ticket: <span className="font-mono">{record.whaticketTicketId}</span>
                          </span>
                        </div>
                        {record.userName && (
                          <div>
                            Atendente:{' '}
                            <span className="font-medium">
                              {record.userId ? `${record.userName} #${record.userId}` : record.userName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                )
              })}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Mostrando {(page - 1) * limit + 1} a{' '}
                {Math.min(page * limit, data.pagination.total)} de{' '}
                {data.pagination.total.toLocaleString()} mensagens
              </p>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={!hasPrevPage}
                  className="h-8 w-8 p-0"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={!hasPrevPage}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>

                <div className="flex items-center gap-1">
                  <span className="text-sm font-medium">{page}</span>
                  <span className="text-sm text-muted-foreground">de</span>
                  <span className="text-sm font-medium">{totalPages || 1}</span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={!hasNextPage}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(totalPages)}
                  disabled={!hasNextPage}
                  className="h-8 w-8 p-0"
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
